/**
 * 워크북 셀 태그 키 계약 — 백엔드 `TaggedKey.__str__`와 같은 규칙.
 *
 * 형식: `{lineItem}|{member1}|{member2}…:{accumulatedType}`
 * - 멤버가 없으면 `|` 이하가 통째로 생략 → `dart_Assets`
 * - 누적 구분이 없으면 `:` 접미사가 생략 → `dart_Assets|dart_SegmentAMember`
 * - 둘 다 있으면 → `dart_Revenue|dart_SegmentAMember:QUARTER_ACC`
 *
 * lineItem에는 기초/기말 변형이 `/BEGIN`·`/END`로 붙는다. 멤버는 정렬해서
 * 이어붙이므로(백엔드 `canonical_axis_key`) 저장 시점의 축 순서와 무관하다.
 *
 * 프리뷰 셀의 id는 CellId 형식(`{period}:{lineItem}:{axis}:{acc}`)으로 올 수도
 * 있다 — 백엔드 `parse_cell_tag_key`가 두 형식을 모두 받기 때문이다. 여기서도
 * 둘 다 같은 정규형으로 접어 비교한다.
 *
 * TaggedKey는 기간을 담지 않는다. 당기/전기가 같은 키가 되므로, 셀을 짚을 때는
 * 반드시 `periodScopedTagKey`로 기간을 함께 묶어야 한다.
 */

/** 백엔드 AccumulatedType. 이 둘만 `:` 접미사가 된다 */
const ACCUMULATED_TYPES = new Set(["ACC", "QUARTER_ACC"]);

/** CellId 형식 판별에 쓰는 PeriodType. 화면은 전기를 PRIOR로도 부른다 */
const PERIOD_TYPES = new Set(["CURRENT", "PREVIOUS", "PRIOR"]);

/** 화면의 PRIOR와 서버의 PREVIOUS는 같은 기간이다 */
export const normalizeTagKeyPeriod = (period: string): string =>
  period === "PRIOR" ? "PREVIOUS" : period;

const buildKey = (
  lineItem: string,
  members: readonly string[],
  accumulatedType: string,
): string => {
  const sorted = [...members].filter(Boolean).sort();
  const base = sorted.length ? `${lineItem}|${sorted.join("|")}` : lineItem;
  return accumulatedType ? `${base}:${accumulatedType}` : base;
};

export interface CellTagKeyParts {
  lineItemId: string;
  /** `BEGIN`·`END`만 lineItem에 변형으로 붙는다. `NONE`은 접미사가 없다 */
  begend?: string | null;
  /** 축이 아니라 멤버 id다 — 백엔드 canonical_axis_key도 멤버로 만든다 */
  memberIds: readonly string[];
  accumulatedType?: string | null;
}

/**
 * 값 동기화 실패 기록에서 워크북 셀 태그 키를 만든다.
 *
 * 모르는 accumulatedType은 붙이지 않는다. 붙이면 셀 쪽 키와 어긋나 아무 셀도
 * 못 찾는데, 그건 엉뚱한 셀에 표식을 붙이는 것보다 낫다.
 */
export const makeCellTagKey = ({
  lineItemId,
  begend,
  memberIds,
  accumulatedType,
}: CellTagKeyParts): string => {
  const variant = begend === "BEGIN" || begend === "END" ? `/${begend}` : "";
  return buildKey(
    `${lineItemId}${variant}`,
    memberIds,
    accumulatedType && ACCUMULATED_TYPES.has(accumulatedType)
      ? accumulatedType
      : "",
  );
};

/**
 * 셀이 들고 있는 키를 정규형으로 접는다. CellId 형식도 받는다.
 *
 * 멤버 순서만 다른 키가 서로 다른 키가 되지 않게 하는 것이 요점이다.
 */
export const normalizeCellTagKey = (raw: string): string => {
  const segments = raw.split(":");
  // CellId 판별은 콜론 4조각 + 첫 조각이 PeriodType — 백엔드와 같은 기준이다.
  if (segments.length === 4 && PERIOD_TYPES.has(segments[0])) {
    const [, lineItem, axis, accumulatedType] = segments;
    return buildKey(
      lineItem,
      axis.split("|"),
      ACCUMULATED_TYPES.has(accumulatedType) ? accumulatedType : "",
    );
  }

  /*
   * `:` 분해는 뒤에서부터. 마지막 조각이 AccumulatedType일 때만 잘라낸다 —
   * 태그 id에 콜론이 들어가도 본문을 통째로 잃지 않는다.
   */
  const cut = raw.lastIndexOf(":");
  const tail = cut >= 0 ? raw.slice(cut + 1) : "";
  const hasAccumulated = cut >= 0 && ACCUMULATED_TYPES.has(tail);
  const [lineItem, ...members] = (
    hasAccumulated ? raw.slice(0, cut) : raw
  ).split("|");
  return buildKey(lineItem, members, hasAccumulated ? tail : "");
};

/** 당기/전기가 같은 태그 키를 갖기 때문에 기간을 묶어야 셀이 하나로 좁혀진다 */
export const periodScopedTagKey = (period: string, tagKey: string): string =>
  `${normalizeTagKeyPeriod(period)}::${tagKey}`;

/** 누적 구분만 떼어낸 키. 누락 검사용 빌더가 만든 셀 키와 맞추는 데 쓴다 */
export const stripAccumulatedType = (tagKey: string): string => {
  const cut = tagKey.lastIndexOf(":");
  if (cut < 0) return tagKey;
  return ACCUMULATED_TYPES.has(tagKey.slice(cut + 1))
    ? tagKey.slice(0, cut)
    : tagKey;
};
